import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import apiClient from './apiClient';
import usePreloaderStore from '../stores/usePreloaderStore';

export function useApiQuery(queryKey, url, options = {}) {
    const { enqueueSnackbar } = useSnackbar();

    return useQuery({
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        queryFn: async () => {
            const response = await apiClient.get(url);
            return response.data;
        },
        ...options,
        meta: {
            onError: (error) => {
                const message = error.response?.data?.error || error.message;
                enqueueSnackbar(message, { variant: 'error' });
            },
        },
    });
}

export function useApiMutation(mutationFn, options = {}) {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const showPreloader = usePreloaderStore((state) => state.show);
    const hidePreloader = usePreloaderStore((state) => state.hide);

    return useMutation({
        mutationFn,
        onError: (error) => {
            const message = error.response?.data?.error || error.message;
            enqueueSnackbar(message, { variant: 'error' });
        },
        onSuccess: (data, variables, context) => {
            if (options.successMessage) {
                enqueueSnackbar(options.successMessage, { variant: 'success' });
            }
            if (options.invalidateQueries) {
                queryClient.invalidateQueries({ queryKey: options.invalidateQueries });
            }
            options.onSuccess?.(data, variables, context);
        },
        ...options,
        // withPreloader: глобальный прелоадер на время запроса. Композируем поверх ...options, чтобы не затирать чужие onMutate/onSettled.
        onMutate: (variables) => {
            if (options.withPreloader) showPreloader();
            return options.onMutate?.(variables);
        },
        onSettled: (data, error, variables, context) => {
            if (options.withPreloader) hidePreloader();
            return options.onSettled?.(data, error, variables, context);
        },
    });
}

export function useApiPost(options = {}) {
    return useApiMutation(
        async ({ url, data, params }) => {
            const response = await apiClient.post(url, data, { params });
            return response.data;
        },
        options
    );
}

export function useApiPut(options = {}) {
    return useApiMutation(
        async ({ url, data, params }) => {
            const response = await apiClient.put(url, data, { params });
            return response.data;
        },
        options
    );
}

export function useApiPatch(options = {}) {
    return useApiMutation(
        async ({ url, data, params }) => {
            const response = await apiClient.patch(url, data, { params });
            return response.data;
        },
        options
    );
}

export function useApiDelete(options = {}) {
    return useApiMutation(
        async ({ url, params }) => {
            const response = await apiClient.delete(url, { params });
            return response.data;
        },
        options
    );
}
